import * as Dialog from "@radix-ui/react-dialog";
import { useRef, useState, type PointerEvent, type ReactNode } from "react";
import { Icon } from "./icons";
import { useFocusReturn } from "./focusReturn";
import { usePrefersReducedMotion } from "./useMediaQuery";
import s from "./bottomsheet.module.css";

/** Drag farther than this many px, or flick down faster than FLICK px/ms, and the sheet closes. */
export const CLOSE_DISTANCE = 110;
export const FLICK_SPEED = 0.6;
const FLICK_MIN_DISTANCE = 24;

/** Pure: should a release at `dy` px after `ms` close the sheet? */
export function shouldClose(dy: number, ms: number): boolean {
  if (dy > CLOSE_DISTANCE) return true;
  return dy >= FLICK_MIN_DISTANCE && ms > 0 && dy / ms >= FLICK_SPEED;
}

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Visually hide the title (the sheet still has an accessible name). */
  hideTitle?: boolean;
}

/**
 * Phone sheet: Radix Dialog underneath (focus trap, Esc, focus back to the opener), grab handle, scrim, 92dvh with inner
 * scroll, drag the handle or header down to close, safe-area padding. No motion under `prefers-reduced-motion`.
 */
export function BottomSheet({ open, onOpenChange, title, description, children, footer, hideTitle }: BottomSheetProps) {
  const focus = useFocusReturn();
  const reduced = usePrefersReducedMotion();
  const [dy, setDy] = useState(0);
  const drag = useRef<{ y: number; t: number } | null>(null);

  const down = (e: PointerEvent<HTMLElement>) => {
    drag.current = { y: e.clientY, t: performance.now() };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e: PointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    setDy(Math.max(0, e.clientY - drag.current.y));
  };
  const up = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const dist = Math.max(0, e.clientY - d.y);
    setDy(0);
    if (shouldClose(dist, performance.now() - d.t)) onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.scrim} data-motion={reduced ? "reduced" : undefined} />
        <Dialog.Content
          className={s.sheet}
          data-motion={reduced ? "reduced" : undefined}
          data-dragging={dy > 0 || undefined}
          style={dy > 0 ? { transform: `translateY(${dy}px)` } : undefined}
          aria-describedby={description ? undefined : undefined}
          {...focus}
        >
          <div className={s.grab} data-testid="sheet-grab" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
            <div className={s.handle} aria-hidden="true" />
            <header className={hideTitle ? s.sr : undefined}>
              <div>
                <Dialog.Title asChild><h2>{title}</h2></Dialog.Title>
                {description ? <Dialog.Description asChild><p>{description}</p></Dialog.Description> : null}
              </div>
            </header>
          </div>
          <Dialog.Close className={s.close} aria-label="Close"><span><Icon name="x" size={16} /></span></Dialog.Close>
          <div className={s.body}>{children}</div>
          {footer ? <footer className={s.foot}>{footer}</footer> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
